import type {
  ConditionalArgument,
  RoutineVariable,
} from '@sb/remote-control/types';
import type { Expression } from '@sb/routine-runner';

// ============================================================================
// Type Guards
// ============================================================================

function isExpression(value: unknown): value is Expression {
  return (
    typeof value === 'object' &&
    value !== null &&
    'kind' in value &&
    (value.kind === 'JavaScript' ||
      value.kind === 'Python' ||
      value.kind === 'constant')
  );
}

function isStepVariable(
  v: RoutineVariable.VariableInformation,
): v is RoutineVariable.StepVariableInformation {
  return v.kind === 'step';
}

function isIOStateVariable(
  v: RoutineVariable.VariableInformation,
): v is RoutineVariable.IOStateVariableInformation {
  return v.kind === 'ioState';
}

function isForceVariable(
  v: RoutineVariable.VariableInformation,
): v is RoutineVariable.ForceVariableInformation {
  return v.kind === 'force';
}

function hasBooleanValues(
  v: RoutineVariable.VariableInformation,
): v is RoutineVariable.VariableInformation & {
  values: RoutineVariable.EnumValue<boolean>[];
} {
  return v.variableKind === 'boolean' && 'values' in v;
}

function hasEnumValues(
  v: RoutineVariable.VariableInformation,
): v is RoutineVariable.VariableInformation & {
  values: RoutineVariable.EnumValue<string>[];
} {
  return v.variableKind === 'enum' && 'values' in v;
}

function hasUnitLabel(
  v: RoutineVariable.VariableInformation,
): v is RoutineVariable.VariableInformation & { unitLabel?: string } {
  return v.variableKind === 'number';
}

// ============================================================================
// Formatters
// ============================================================================

type VariableFormatter = (
  variable: RoutineVariable.VariableInformation,
) => string;

type ValueFormatter = (
  value: ConditionalArgument.RightOperand,
  variable: RoutineVariable.VariableInformation,
) => string;

/**
 * Formatters for different variable kinds
 */
const variableFormatters: Record<string, VariableFormatter> = {
  step: (v) => {
    if (!isStepVariable(v)) return v.displayName;

    return `${v.stepPosition}. ${v.stepName}: ${v.displayName}`;
  },
  ioState: (v) => {
    if (!isIOStateVariable(v)) return v.displayName;

    return `${v.ioKind} ${v.port}`;
  },
  gripperState: (v) => `Gripper: ${v.displayName}`,
  force: (v) => {
    if (!isForceVariable(v)) return v.displayName;

    return `${v.sensor}: ${v.displayName}`;
  },
  default: (v) => v.displayName,
};

/**
 * Formatters for different value types
 *
 * Maps raw primitive values to human-readable display strings by looking up
 * the corresponding display name or adding unit labels.
 *
 * @example
 * // Boolean: maps boolean value to display name
 * // value = true, variable.values = [{ value: true, displayName: "Open" }, { value: false, displayName: "Closed" }]
 * // => "Open"
 *
 * @example
 * // Enum: maps enum value to display name
 * // value = "high", variable.values = [{ value: "high", displayName: "High" }, { value: "low", displayName: "Low" }]
 * // => "High"
 *
 * @example
 * // Number: appends unit label if available
 * // value = 5, variable.unitLabel = "mm"
 * // => "5 mm"
 */
const valueFormatters: Record<string, ValueFormatter> = {
  boolean: (value, variable) => {
    if (!hasBooleanValues(variable)) return String(value);
    const boolValue = variable.values.find((v) => v.value === value);

    return boolValue?.displayName || String(value);
  },

  enum: (value, variable) => {
    if (!hasEnumValues(variable)) return String(value);
    const enumValue = variable.values.find((v) => v.value === value);

    return enumValue?.displayName || String(value);
  },

  number: (value, variable) => {
    if (!hasUnitLabel(variable)) return String(value);
    const str = String(value);

    return variable.unitLabel ? `${str} ${variable.unitLabel}` : str;
  },

  default: (value) => String(value),
};

/**
 * Formats expression values (JavaScript/Python expressions)
 */
function formatExpressionValue(value: Expression): string {
  if (value.kind === 'JavaScript' || value.kind === 'Python') {
    return value.expression;
  }

  if (value.kind === 'constant') {
    return String(value.value);
  }

  return '';
}

/**
 * Formats a single condition into a human-readable string
 *
 * @example
 * formatCondition({
 *   variable: { kind: 'gripperState', displayName: 'isOpen', ... },
 *   operator: { operator: '==', displayName: '==' },
 *   value: true
 * })
 * // => "Gripper: isOpen == true"
 */
export function formatCondition(
  condition: ConditionalArgument.ConditionalBuilderState,
): string {
  const { variable, operator, value } = condition;

  if (!variable || !operator || value === null || value === undefined) {
    return '';
  }

  // Format variable name using registry
  const formatVariable =
    variableFormatters[variable.kind] || variableFormatters.default;

  const variableName = formatVariable(variable);

  // Format operator
  const operatorSymbol = operator.displayName || operator.operator;

  // Format value
  let valueStr = '';

  // Expressions can be values for ANY variable type
  if (isExpression(value)) {
    valueStr = formatExpressionValue(value);
  } else {
    const formatValue =
      valueFormatters[variable.variableKind] || valueFormatters.default;

    valueStr = formatValue(value, variable);
  }

  return `${variableName} ${operatorSymbol} ${valueStr}`;
}

/**
 * Formats an AND group of conditions into a readable string
 *
 * @example
 * // Single condition
 * formatConditionGroup([condition1]) // => "Gripper: isOpen == true"
 *
 * @example
 * // Multiple conditions
 * formatConditionGroup([condition1, condition2])
 * // => "Gripper: isOpen == true AND Input 1 > 5"
 */
export function formatConditionGroup(
  conditions: ConditionalArgument.ConditionalBuilderState[],
): string {
  const formattedConditions = conditions
    .map(formatCondition)
    .filter((s) => s.length > 0);

  return formattedConditions.join(' AND ');
}
